import { useState } from "react";
import { Mail, Loader2, Send } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { sendTestPaymentEmail } from "@/lib/email/test-payment-email.functions";

const PLANS = [
  { value: "full_support", label: "Full Support" },
  { value: "basic_support", label: "Basic Support" },
  { value: "refusal_support", label: "Refusal Support" },
] as const;

export function TestPaymentEmailCard() {
  const send = useServerFn(sendTestPaymentEmail);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [planKey, setPlanKey] = useState<(typeof PLANS)[number]["value"]>("full_support");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) {
      toast.error("Enter a test email address");
      return;
    }
    setSending(true);
    try {
      const res = await send({ data: { email: email.trim(), name: name.trim() || undefined, planKey } });
      if (res.sent) {
        toast.success(`Test email queued to ${res.recipient}`, {
          description: "Should arrive within ~30 seconds via notify.welivdit.com.",
        });
      } else {
        toast.error(`Email not sent`, { description: `Reason: ${res.reason}` });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to send test email", { description: (err as Error).message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-3xl border border-dashed border-amber-400/60 bg-amber-50/40 dark:bg-amber-950/10 p-6">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <Mail className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-semibold">Test payment confirmation email</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Sends a mock payment confirmation via <span className="font-mono">notify.welivdit.com</span> to verify branding & deliverability. Temporary - remove before going live.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <input
          type="email"
          placeholder="test@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm sm:col-span-2"
          autoComplete="off"
        />
        <input
          type="text"
          placeholder="Recipient name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
          autoComplete="off"
        />
        <select
          value={planKey}
          onChange={(e) => setPlanKey(e.target.value as typeof planKey)}
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm"
        >
          {PLANS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <Button onClick={handleSend} disabled={sending} className="sm:col-span-2 gap-2">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Sending…" : "Send Test Payment Email"}
        </Button>
      </div>
    </div>
  );
}
